import React, { useContext, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { formatPrice } from 'utils/price';
import { IconInfoCircle } from '@tabler/icons-react';

import { GET_PRODUCTS_STOCK } from 'data/queries';

import { colors } from 'styles/theme';
import { Tooltip } from 'styles';

import { requiresStripePaymentMethod } from 'constants/organization';
import { CREATED, INPROGRESS, VALIDATED } from 'constants/states-app';
import { PRODUCT_FLOW } from 'constants/product-constants';
import { UserContext } from 'constants/user-context';
import {
    ORDER_BAKERY_MIN_AMOUNT,
    ORDER_CATERER_MIN_AMOUNT,
    ORDER_DRY_MIN_AMOUNT,
    ORDER_FRESH_MIN_AMOUNT,
    ORDER_FRUIT_MIN_AMOUNT,
    ORDER_MIN_AMOUNT,
} from 'constants/order';

import { TotemButton } from 'ui/Button';
import { StockWarningPopup } from 'pages/Totem/Aside/Basket/StockWarningPopup';
import { BillingInfoUnavailable } from 'components/BillingInfoUnavailable';
import { ProgressBar } from 'components/ProgressBar';

import { HandleNotification } from 'utils';
import { orderValidatedCacheHandler } from 'utils/cache/orderValidatedCacheHandler';
import { orderUpdatedCacheHandler } from 'utils/cache/orderUpdatedCacheHandler';
import { totemUpdatedCacheHandler } from 'utils/cache/totemUpdatedCacheHandler';
import { remainingTimeForValidation } from 'utils/time';

import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';

import { formatDateForDisplay } from 'constants/date-constants';
import { TotemClosedDaysContext } from 'contexts/TotemClosedDaysContext';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import { usePriceVisibility } from 'hooks/usePriceVisibility';
import { roundPrice } from 'utils/orders';
import {
    useCheckoutMutation,
    useDiscardMutation,
    useGetLatestTermsRecordQuery,
    useUpdateAcceptedTermsMutation,
    useUpdateOrderWithStockQuantitiesMutation,
    useValidateMutation,
} from 'data/__generated__';

interface BasketFooterProps {
    currentTotem: any;
    order: any;
    ordersOfTotem: any;
    setIsBillingAsideOpen: (open: boolean) => void;
    setIsDeliveryAccessInstructionsAsideOpen: (open: boolean) => void;
}

export const BasketFooter: React.FC<BasketFooterProps> = ({
    currentTotem,
    order,
    ordersOfTotem,
    setIsBillingAsideOpen,
    setIsDeliveryAccessInstructionsAsideOpen,
}) => {
    const navigate = useNavigate();

    const totemClosedDays = useContext(TotemClosedDaysContext);
    const { organization } = useContext(UserContext);
    const { isPriceVisible, keepVisiblePriceProducts } = usePriceVisibility();
    // Requête dédiée (Odoo) : son échec ne doit pas empêcher le panier de s'afficher ni de se modifier.
    const { billingInfo, isBillingInfoLoading, billingInfoError, refetchBillingInfo } = useOrganizationBillingInfo();

    const [isValidationLoading, setIsValidationLoading] = useState(false);
    const [getProductsStockCounter, setGetProductsStockCounter] = useState(0);

    const apolloClient = useApolloClient();

    const { data: termsRecordData, loading: areTermsRecordLoading } = useGetLatestTermsRecordQuery();

    const [discard] = useDiscardMutation();
    const [updateOrderWithStockQuantities] = useUpdateOrderWithStockQuantitiesMutation();
    const [updateAcceptedTerms] = useUpdateAcceptedTermsMutation();
    const [checkout, { loading: isCheckoutLoading }] = useCheckoutMutation();
    const [validate, { loading: isValidateLoading }] = useValidateMutation();

    async function handleStockAdjustment(
        productsFromOrderWithInsufficientQuantity: Array<{ productId: string; availableStock: number }>,
        toastId: string,
    ) {
        const productsStockToUpdate = productsFromOrderWithInsufficientQuantity.map(({ productId, availableStock }) => {
            return { productId, availableStock };
        });
        const response = await updateOrderWithStockQuantities({
            variables: {
                orderId: order._id,
                productsStockToUpdate,
            },
        });
        const updatedOrder = response?.data?.updateOrderWithStockQuantities;
        toast.remove(toastId);
        toast.success('Les quantités de produits ont été mises à jour', {
            duration: 2000,
        });
        orderUpdatedCacheHandler({ apolloClient, totemId: currentTotem._id, orderId: order._id, updatedOrder });
    }

    interface ProductsStockDataItem {
        productId: string;
        availableStock: number;
        imageUrl?: string;
        brand?: string;
        volume?: number;
        displayKg?: boolean;
        weightWithoutPackaging?: number;
    }

    interface OrderProduct {
        _id: string;
        name: string;
        quantity: number;
        [key: string]: any;
    }

    interface ProductsStockQueryResult {
        productsStock: ProductsStockDataItem[];
    }

    function handleProductsStock(data: ProductsStockQueryResult) {
        const { productsStock: productsStockData } = data;
        setGetProductsStockCounter(getProductsStockCounter + 1);
        const insufficientProducts = productsStockData.reduce((acc: any[], stock: ProductsStockDataItem) => {
            const { productId, availableStock, imageUrl, brand, volume, displayKg, weightWithoutPackaging } = stock;
            const orderProduct = order.products.find((product: OrderProduct) => product._id === productId);
            const { name, quantity: requestedQuantity } = orderProduct;
            // stock online can be negative, but we want to avoid having negative quantities in the order
            const correctedStockOnline = availableStock < 0 ? 0 : availableStock;
            if (requestedQuantity > correctedStockOnline) {
                acc.push({
                    requestedQuantity,
                    productId,
                    name,
                    brand,
                    volume,
                    imageUrl,
                    availableStock: correctedStockOnline,
                    displayKg,
                    weightWithoutPackaging,
                });
            }
            return acc;
        }, []);
        if (!insufficientProducts.length) {
            hasConfirmedTotemOrders ? handleOrderValidation() : handleOrderCheckout();
        } else {
            toast.custom(
                (t: { id: string }) => (
                    <StockWarningPopup
                        productsWithInsufficientQuantity={insufficientProducts}
                        confirmStock={() => handleStockAdjustment(insufficientProducts, t.id)}
                        handleClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                ),
                {
                    duration: Infinity,
                },
            );
        }
        setIsValidationLoading(false);
    }
    const [getProductsStock, { loading: isProductsStockLoading }] = useLazyQuery(GET_PRODUCTS_STOCK, {
        onError: (error) => HandleNotification(error),
        onCompleted: handleProductsStock,
        fetchPolicy: 'no-cache',
    });

    const { hasConfirmedTotemOrders } = ordersOfTotem;
    const isLoading = isValidateLoading || isProductsStockLoading || isCheckoutLoading || areTermsRecordLoading;
    const hasOrganizationAcceptedLastTerms =
        organization?.acceptedTerms?.termsRecordId === termsRecordData?.latestTermsRecord._id;
    const orderCanBeDiscarded =
        !order?.isPunctual &&
        (order?.stateHistory.some(({ state }: { state: string }) => state === VALIDATED) ||
            order?.first_order === false) &&
        order?.state === INPROGRESS;

    const orderMinAmount = currentTotem?.minimumOrderAmountInfo.minimumTotalAmount ?? ORDER_MIN_AMOUNT;

    const bakeryMinAmount = currentTotem?.minimumOrderAmountInfo.minimumBakeryAmount ?? ORDER_BAKERY_MIN_AMOUNT;
    const dryMinAmount = currentTotem?.minimumOrderAmountInfo.minimumDryAmount ?? ORDER_DRY_MIN_AMOUNT;
    const freshMinAmount = currentTotem?.minimumOrderAmountInfo.minimumFreshAmount ?? ORDER_FRESH_MIN_AMOUNT;
    const fruitMinAmount = currentTotem?.minimumOrderAmountInfo.minimumFruitAmount ?? ORDER_FRUIT_MIN_AMOUNT;
    const catererMinAmount = currentTotem?.minimumOrderAmountInfo.minimumCatererAmount ?? ORDER_CATERER_MIN_AMOUNT;

    // Les minimums de commande sont affichés au client ET conditionnent le bouton « Valider » : ils se
    // calculent donc sur ce que le client VOIT. Un produit à prix masqué pesait 0 du temps de la gamme
    // à 0 € ; il ne doit pas se mettre à peser maintenant que les vrais prix sont revenus — sinon un
    // « Montant minimum épicerie non atteint : 25€/40€ » rend le prix qu'on masque deux lignes plus haut.
    //
    // CE QUE ÇA IMPLIQUE, ET QUI EST VOULU : un panier composé UNIQUEMENT de produits à prix masqué
    // reste à 0 € de montant visible, donc sous le minimum global, donc non validable. C'est la
    // contrepartie assumée de l'invariant — et le comportement qu'avait déjà la gamme à 0 €.
    // À noter, les minimums PAR FLUX ne bloquent pas dans ce cas (`!freshAmount || …` les neutralise
    // quand la catégorie est vide) : seul le minimum global s'applique. Ne pas « corriger » en
    // repassant sur `order.prices.price` : ça rouvrirait la fuite.
    const visibleProducts: OrderProduct[] = keepVisiblePriceProducts(order?.products ?? []);
    const sumAmountOfFlow = (flow: string) =>
        visibleProducts
            .filter((product) => product.flow === flow)
            .reduce((acc: number, product: OrderProduct) => acc + product.unitPrice * product.quantity, 0);

    const freshAmount = sumAmountOfFlow(PRODUCT_FLOW.FRESH);
    const dryAmount = sumAmountOfFlow(PRODUCT_FLOW.DRY);
    const fruitAmount = sumAmountOfFlow(PRODUCT_FLOW.FRUIT);
    const bakeryAmount = sumAmountOfFlow(PRODUCT_FLOW.BAKERY);
    const catererAmount = sumAmountOfFlow(PRODUCT_FLOW.CATERER);
    // Le montant total, lui, se DÉDUIT de la valeur serveur au lieu d'être resommé : `order.prices.price`
    // est la référence qui conditionne la validation, et la resommer ligne à ligne y introduirait une
    // dérive d'arrondi pour tous les clients, y compris ceux qu'aucune règle ne vise. Sans produit
    // masqué, on retranche zéro et la valeur serveur est conservée à l'identique.
    const hiddenProductsAmount = roundPrice(
        (order?.products ?? []).reduce(
            (acc: number, product: OrderProduct) =>
                isPriceVisible(product._id) ? acc : acc + product.unitPrice * product.quantity,
            0,
        ),
    );
    // Borné à zéro : ce montant s'affiche ET conditionne la validation ; un négatif (totaux serveur et
    // lignes divergents) donnerait un « -12€/60€ » incompréhensible sous la barre de progression.
    const visibleProductsAmount = Math.max(order.prices.price - hiddenProductsAmount, 0);

    const orderMinAmountsTooltipText =
        "Un minimum de commande est imposé par catégorie afin d'éviter le transport de caisses de livraison peu remplies. Aidez nous à limiter les émissions de CO2 en regroupant vos achats de produits fruits/épicerie/frais/matin.";

    const hasOrderMinAmounts =
        visibleProductsAmount >= orderMinAmount &&
        (!freshAmount || freshAmount >= freshMinAmount) &&
        (!dryAmount || dryAmount >= dryMinAmount) &&
        (!fruitAmount || fruitAmount >= fruitMinAmount) &&
        (!bakeryAmount || bakeryAmount >= bakeryMinAmount) &&
        (!catererAmount || catererAmount >= catererMinAmount);

    const { fullString: remainingTimeForValidationAsFullString, validationLimitDateIsPast } =
        remainingTimeForValidation({
            deliveryDate: order?.date_delivery,
            firstOrder: order?.first_order,
            hasBeenValidated: order?.stateHistory.some(({ state }: { state: string }) => state === VALIDATED),
            isPunctual: order?.isPunctual,
            state: order?.state,
            totemClosedDays,
        });

    async function handleOrderValidation() {
        try {
            const response = await validate({
                variables: {
                    orderId: order._id,
                },
            });
            const updatedOrder = response?.data?.validate.order;
            const updatedOrganization = response?.data?.validate.organization;

            track(analyticsEvents.VALIDATE_ORDER, {
                [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: order.isPunctual,
            });

            orderValidatedCacheHandler({
                apolloClient,
                orderId: order._id,
                totemId: currentTotem._id,
                updatedOrder,
                updatedOrganization,
            });

            toast.success(
                `Votre commande est validée et vous sera livrée le ${formatDateForDisplay(order.date_delivery)}`,
                {
                    duration: 4000,
                },
            );
            navigate(`/totem/${currentTotem._id}`, { replace: true, relative: 'path' });
            setIsValidationLoading(false);
            return;
        } catch (error) {
            setIsValidationLoading(false);
            HandleNotification(error);
        }
    }

    async function handleOrderCheckout() {
        try {
            const response = await checkout({
                variables: {
                    data: {
                        orderId: order._id,
                        oldState: order.state,
                    },
                },
            });
            const updatedOrder = response?.data?.checkout.order;
            const updatedOrganization = response?.data?.checkout.organization;
            const updatedTotem = response?.data?.checkout.totem;
            orderValidatedCacheHandler({
                apolloClient,
                orderId: order._id,
                totemId: currentTotem._id,
                updatedOrder,
                updatedOrganization,
            });
            totemUpdatedCacheHandler({
                apolloClient,
                currentTotem,
                totemUpdatedValues: updatedTotem,
            });
            track(analyticsEvents.CONFIRM_ORDER, {
                [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.CHECKOUT_BASKET,
                [analyticsPropertiesConstants.ORDER.IS_PUNCTUAL]: order.isPunctual,
            });
            toast.success(
                `Votre commande est validée et vous sera livrée le ${formatDateForDisplay(order.date_delivery)}`,
                {
                    duration: 4000,
                },
            );
            navigate(`/totem/${currentTotem._id}`, { relative: 'path' });
            setIsValidationLoading(false);
        } catch (error) {
            setIsValidationLoading(false);
            HandleNotification(error);
        }
    }

    async function handleNext() {
        try {
            if (!isValidationLoading) {
                setIsValidationLoading(true);
                const productIds = order?.products?.map((product: OrderProduct) => product?._id);
                // getProductStock => set insufficient products (if any) and show popup, if validated => handleProductsStock => validateProducts
                getProductsStock({
                    variables: {
                        productIds,
                        orderId: order?._id,
                        getProductsStockCounter, //dirty fix to make lazyQuery fetch with same parameters twice
                    },
                });
            }
            // user is redirected to the dashboard page from the call to `getProductsStock` mutation
            if (!hasOrganizationAcceptedLastTerms) {
                await updateAcceptedTerms({
                    variables: {
                        hasAcceptedTerms: true,
                    },
                });
            }
            return;
        } catch (error) {
            HandleNotification(error);
        }
    }

    async function handleDiscardOrderChanges() {
        if (!order.isPunctual) {
            const response = await discard({
                variables: {
                    orderId: order._id,
                },
            });
            const updatedOrder = response?.data?.discard;
            orderUpdatedCacheHandler({ apolloClient, totemId: currentTotem._id, orderId: order._id, updatedOrder });
            navigate(`/totem/${currentTotem._id}`, { relative: 'path' });
            return;
        }
    }

    const hasDefinedDeliveryInstructions = !!currentTotem.address && !!currentTotem.address_details;
    const hasDefinedBillingInfo = !!(
        billingInfo?.invoiceName &&
        billingInfo?.invoiceEmail &&
        billingInfo?.invoiceAddress &&
        billingInfo?.invoiceZipcode &&
        billingInfo?.invoiceCity &&
        billingInfo?.invoiceCountry
    );
    // Rien à enregistrer quand Totem ne prélève pas (Odoo encaisse, ou personne) : sans ce court-circuit
    // la validation du panier restait bloquée sur « Ajouter les informations de facturation ».
    const hasAddedAPaymentMethod =
        !requiresStripePaymentMethod(organization?.paymentType) || !!billingInfo?.defaultPaymentMethod;
    // Facturation indisponible = état inconnu, pas « absent » : on garde les montants minimums visibles et
    // on remplace le bouton de validation par un message d'erreur cantonné à cette section.
    const shouldDisplayMinAmounts = hasDefinedDeliveryInstructions && (hasAddedAPaymentMethod || !!billingInfoError);
    return (
        <>
            <FooterContainer>
                {[CREATED, INPROGRESS].includes(order.state) && !validationLimitDateIsPast && (
                    <FooterInfoText>{remainingTimeForValidationAsFullString}</FooterInfoText>
                )}
                {order.state === VALIDATED && (
                    <FooterInfoText>
                        Votre commande est validée et vous sera livrée le {formatDateForDisplay(order.date_delivery)}
                    </FooterInfoText>
                )}
                {shouldDisplayMinAmounts ? (
                    <>
                        {fruitAmount && fruitAmount < fruitMinAmount ? (
                            <MinAmountContainer>
                                <MinAmountTextContainer>
                                    <MinAmountText color={colors.lightRed}>
                                        Montant minimum <MinAmountTextBold>fruits</MinAmountTextBold> non atteint :{' '}
                                        {formatPrice(fruitAmount, { withoutSpace: true })}/
                                        {formatPrice(fruitMinAmount, { withoutSpace: true })}
                                    </MinAmountText>
                                    <TooltipIcon
                                        color={colors.pantinGrey}
                                        data-tip={orderMinAmountsTooltipText}
                                        data-for="tooltipMinAmountFruit"
                                        size={20}
                                    />
                                    <TooltipComponent
                                        id="tooltipMinAmountFruit"
                                        type="dark"
                                        effect="solid"
                                        place="right"
                                        multiline={true}
                                    />
                                </MinAmountTextContainer>
                                <ProgressBar max={fruitMinAmount} value={fruitAmount} $isErrorStyle />
                            </MinAmountContainer>
                        ) : null}
                        {dryAmount && dryAmount < dryMinAmount ? (
                            <MinAmountContainer>
                                <MinAmountTextContainer>
                                    <MinAmountText color={colors.lightRed}>
                                        Montant minimum <MinAmountTextBold>épicerie</MinAmountTextBold> non atteint :{' '}
                                        {formatPrice(dryAmount, { withoutSpace: true })}/
                                        {formatPrice(dryMinAmount, { withoutSpace: true })}
                                    </MinAmountText>
                                    <TooltipIcon
                                        color={colors.pantinGrey}
                                        data-tip={orderMinAmountsTooltipText}
                                        data-for="tooltipMinAmountDry"
                                        size={20}
                                    />
                                    <TooltipComponent
                                        id="tooltipMinAmountDry"
                                        type="dark"
                                        effect="solid"
                                        place="right"
                                        multiline={true}
                                    />
                                </MinAmountTextContainer>
                                <ProgressBar max={dryMinAmount} value={dryAmount} $isErrorStyle />
                            </MinAmountContainer>
                        ) : null}
                        {freshAmount && freshAmount < freshMinAmount ? (
                            <MinAmountContainer>
                                <MinAmountTextContainer>
                                    <MinAmountText color={colors.lightRed}>
                                        Montant minimum <MinAmountTextBold>produits frais</MinAmountTextBold> non
                                        atteint : {formatPrice(freshAmount, { withoutSpace: true })}/
                                        {formatPrice(freshMinAmount, { withoutSpace: true })}
                                    </MinAmountText>
                                    <TooltipIcon
                                        color={colors.pantinGrey}
                                        data-tip={orderMinAmountsTooltipText}
                                        data-for="tooltipMinAmountFresh"
                                        size={20}
                                    />
                                    <TooltipComponent
                                        id="tooltipMinAmountFresh"
                                        type="dark"
                                        effect="solid"
                                        place="right"
                                        multiline={true}
                                    />
                                </MinAmountTextContainer>
                                <ProgressBar max={freshMinAmount} value={freshAmount} $isErrorStyle />
                            </MinAmountContainer>
                        ) : null}
                        {bakeryAmount && bakeryAmount < bakeryMinAmount ? (
                            <MinAmountContainer>
                                <MinAmountTextContainer>
                                    <MinAmountText color={colors.lightRed}>
                                        Montant minimum <MinAmountTextBold>matin</MinAmountTextBold> non atteint :{' '}
                                        {formatPrice(bakeryAmount, { withoutSpace: true })}/
                                        {formatPrice(bakeryMinAmount, { withoutSpace: true })}
                                    </MinAmountText>
                                    <TooltipIcon
                                        color={colors.pantinGrey}
                                        data-tip={orderMinAmountsTooltipText}
                                        data-for="tooltipMinAmountBakery"
                                        size={20}
                                    />
                                    <TooltipComponent
                                        id="tooltipMinAmountBakery"
                                        type="dark"
                                        effect="solid"
                                        place="right"
                                        multiline={true}
                                    />
                                </MinAmountTextContainer>
                                <ProgressBar max={bakeryMinAmount} value={bakeryAmount} $isErrorStyle />
                            </MinAmountContainer>
                        ) : null}
                        {visibleProductsAmount < orderMinAmount ? (
                            <MinAmountContainer>
                                <MinAmountText color={colors.lightRed}>
                                    Montant minimum <MinAmountTextBold>des produits</MinAmountTextBold> non atteint :{' '}
                                    {formatPrice(visibleProductsAmount, { withoutSpace: true })}/
                                    {formatPrice(orderMinAmount, { withoutSpace: true })}
                                </MinAmountText>
                                <ProgressBar max={orderMinAmount} value={visibleProductsAmount} $isErrorStyle />
                            </MinAmountContainer>
                        ) : null}
                    </>
                ) : null}
                {[CREATED, INPROGRESS].includes(order.state) &&
                    (billingInfoError ? (
                        <BillingInfoUnavailable onRetry={refetchBillingInfo} />
                    ) : !hasDefinedDeliveryInstructions ? (
                        <TotemButton
                            $alignSelf="stretch"
                            $margins={[1, 0, 0.5, 0]}
                            $fontSize="1em"
                            data-test="set-delivery-info"
                            onClick={() => setIsDeliveryAccessInstructionsAsideOpen(true)}
                        >
                            Définir les instructions de livraison
                        </TotemButton>
                    ) : isBillingInfoLoading ? (
                        // Les infos de facturation se chargent dans une requête À PART de l'organisation : tant
                        // qu'elle est en vol, on ne SAIT PAS si elles existent. Trancher ici afficherait « Ajouter
                        // les informations de facturation » à un client qui les a déjà, puis basculerait sur la
                        // validation — un bouton fantôme, cliquable, qui ouvre un panneau pour rien.
                        <TotemButton $alignSelf="stretch" $margins={[1, 0, 0.5, 0]} $fontSize="1em" disabled>
                            <ClipLoader color="white" size="16px" />
                        </TotemButton>
                    ) : !(hasAddedAPaymentMethod && hasDefinedBillingInfo) ? (
                        <TotemButton
                            $alignSelf="stretch"
                            $margins={[1, 0, 0.5, 0]}
                            $fontSize="1em"
                            data-test="add-payment-method"
                            onClick={() => setIsBillingAsideOpen(true)}
                        >
                            Ajouter les informations de facturation
                        </TotemButton>
                    ) : (
                        <>
                            {orderCanBeDiscarded && (
                                <TotemButton
                                    $alignSelf="stretch"
                                    $margins={[0.5, 0]}
                                    $type="secondary"
                                    $fontSize="1em"
                                    data-test="discard-order-changes"
                                    onClick={handleDiscardOrderChanges}
                                >
                                    {isLoading ? <ClipLoader color="white" size="16px" /> : 'Annuler les modifications'}
                                </TotemButton>
                            )}
                            <TotemButton
                                $alignSelf="stretch"
                                $margins={[1, 0, 0.5, 0]}
                                $fontSize="1em"
                                disabled={isLoading || !hasOrderMinAmounts || validationLimitDateIsPast}
                                data-test="validate-order"
                                onClick={handleNext}
                            >
                                {isLoading ? (
                                    <ClipLoader color="white" size="16px" />
                                ) : orderCanBeDiscarded ? (
                                    'Valider les modifications'
                                ) : (
                                    'Valider ma commande'
                                )}
                            </TotemButton>
                        </>
                    ))}
                {/* Mention des CGV : affichée dès que la commande est validable, donc exactement quand le
                    bouton « Valider ma commande » apparaît. Elle était conditionnée au moyen de paiement
                    Stripe par défaut — invisible pour toute organisation que Totem ne prélève pas (`Odoo`,
                    `NoPayment`), alors que `handleNext` enregistre l'acceptation dans tous les cas : le
                    client acceptait des CGV qu'on ne lui avait jamais montrées. */}
                {hasAddedAPaymentMethod &&
                    hasDefinedBillingInfo &&
                    [CREATED, INPROGRESS].includes(order.state) &&
                    !hasOrganizationAcceptedLastTerms && (
                        <CheckBoxLine>
                            <FooterInfoText>
                                En validant ma commande, j'accepte les{' '}
                                <a
                                    href={
                                        termsRecordData?.latestTermsRecord?.termsOfUseUrl &&
                                        termsRecordData.latestTermsRecord.termsOfUseUrl
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {hasConfirmedTotemOrders && 'nouvelles '}conditions générales de vente
                                </a>
                            </FooterInfoText>
                        </CheckBoxLine>
                    )}
            </FooterContainer>
        </>
    );
};

const FooterContainer = styled.footer`
    background: white;
    box-shadow: rgb(123 104 104 / 20%) 0px -4px 1rem;
    z-index: 2147483003;
    width: 100%;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const FooterInfoText = styled.div`
    font-size: 0.9rem;
    text-align: center;
    padding: 0.5rem 0 0.5rem 0;
    color: ${({ color }) => color};
    a {
        text-decoration: underline;
    }
`;

const MinAmountContainer = styled.div`
    margin-top: 10px;
    margin-bottom: 10px;
    width: 100%;
`;

const MinAmountTextContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2px;
`;

const MinAmountText = styled.div`
    font-size: 1rem;
    text-align: center;
    color: ${({ color }) => color};
`;

const MinAmountTextBold = styled.span`
    font-weight: 800;
`;

const TooltipIcon = styled(IconInfoCircle)`
    margin-left: 5px;
`;

const TooltipComponent = styled(Tooltip)`
    max-width: 50%;
`;

const CheckBoxLine = styled.div`
    display: flex;
    align-items: end;
`;
